import React, { useCallback, useEffect, useState } from "react"
import Link from "components/atoms/Link/Link"
import Status from "components/atoms/Status/Status"
import { InfoContent, InfoWrapper, Wrapper } from "components/shared/InfoComponents"
import PlatformLink from "components/shared/PlatformLink"
import { useNavigate, useParams } from "react-router-dom"
import { AdminProfileService } from "services/api-service"
import { AdminProfileType } from "services/api-service/types"
import { SentryService } from "services/SentryService"
import { Box } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import Spinner from "ui/components/atoms/spinner"
import Tag from "ui/components/atoms/tag"
import { H1, Label, P } from "ui/components/atoms/typography"
import Modal from "ui/components/molecules/modal"
import SwitchRow from "ui/components/molecules/switch-row"
import { translate } from "ui/data"
import { PROFILE_FEATURE_FLAGS_ARRAY } from "ui/data/ProfileInformations"
import { addSpacesFromPhoneNumber } from "ui/utils/string"

const AdminProfileModal = () => {
  const navigate = useNavigate()
  const { profileId } = useParams()

  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [changing, setChanging] = useState(false)
  const [data, setData] = useState<null | AdminProfileType>(null)
  const [pendingFlag, setPendingFlag] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const profile = await AdminProfileService.getProfile(`${profileId}`)
        setData(profile)
        setLoading(false)
      } catch (e) {
        SentryService.error("[ERROR AdminProfileService.getProfile]:", e)
      }
    }
    fetchData()
  }, [profileId])

  const handleDelete = useCallback(async () => {
    if (window.confirm(`Czy na pewno chcesz usunąć profil ${data?.name}?`)) {
      try {
        setDeleting(true)
        await AdminProfileService.deleteProfile(`${profileId}`)
        setDeleting(false)
        navigate("/", {
          replace: true,
        })
      } catch (e) {
        SentryService.error("[ERROR AdminProfileService.deleteProfile]:", e)
      }
    }
  }, [data?.name, profileId, navigate])

  const handleToggleFlag = useCallback(
    async ({ name, value }: { name: string; value: boolean }) => {
      if (!profileId || pendingFlag) return
      setPendingFlag(name)
      try {
        const updated = value
          ? await AdminProfileService.setFeatureFlag(profileId, name)
          : await AdminProfileService.unsetFeatureFlag(profileId, name)
        setData(updated)
      } catch (e) {
        SentryService.error("[ERROR AdminProfileService.toggleFeatureFlag]:", e)
        window.alert("Nie udało się zmienić stanu funkcji. Spróbuj ponownie.")
      } finally {
        setPendingFlag(null)
      }
    },
    [profileId, pendingFlag],
  )

  const handleGenerateToken = useCallback(async () => {
    try {
      setGenerating(true)
      const token = await AdminProfileService.generateToken(`${profileId}`)
      const url = `https://panel.jobintrans.eu/panel?token=${token}`

      navigator.clipboard.writeText(url)
      alert("Link został skopiowany")
    } catch (e) {
      SentryService.error("[ERROR AdminProfileService.generateToken]:", e)
    } finally {
      setGenerating(false)
    }
  }, [profileId])

  return (
    <Modal
      show={true}
      setShow={() => navigate("/panel/profiles")}
      type="side"
      title="Informacje profilu"
      $maxWidth="700px"
    >
      <>
        {loading || !data ? (
          <Box $marginTop="XL">
            <Spinner />
          </Box>
        ) : (
          <Wrapper>
            <Box $marginBottom="M">
              <Status label={data.status} />
              <H1 $marginTop="XS" $marginBottom="0">
                {data.name}
              </H1>
              {data.type === "company" ? (
                <Box $marginTop="XXS">
                  <PlatformLink status={data.isVisible} to={`/profiles/${data.slug}`} />
                </Box>
              ) : (
                <Tag
                  $type="secondary"
                  $showBorder={false}
                  $background="background"
                  label="Konto prywatne"
                  $marginTop="XS"
                />
              )}
            </Box>
            <InfoWrapper>
              <InfoContent>
                <P label="Email" $marginBottom="0">
                  {data.email}
                </P>
                <P label="Numer telefonu" $marginBottom="0">
                  {addSpacesFromPhoneNumber(data.phone)}
                </P>
                {data.type === "company" && (
                  <>
                    {data.website && (
                      <P label="Strona internetowa" $marginBottom="0">
                        {data.website}
                      </P>
                    )}
                    {data.companyIndustrie && (
                      <P label="Branża firmy" $marginBottom="0">
                        {translate(data.companyIndustrie)}
                      </P>
                    )}
                    {data.companySize && (
                      <P label="Branża firmy" $marginBottom="0">
                        {translate(data.companySize)}
                      </P>
                    )}
                  </>
                )}
              </InfoContent>
            </InfoWrapper>
            <InfoWrapper>
              <Label $display="block" $marginBottom="XS">
                Adres
              </Label>
              <InfoContent>
                <P label="Adres" $marginBottom="0">
                  {data.address.address}
                </P>
                <P label="Miasto" $marginBottom="0">
                  {data.address.city}
                </P>
                <P label="Kod pocztowy" $marginBottom="0">
                  {data.address.postCode}
                </P>
                <P label="Kraj" $marginBottom="0">
                  {translate(data.address.countryCode.toUpperCase())}
                </P>
              </InfoContent>
            </InfoWrapper>
            {data.company && (
              <InfoWrapper>
                <Label $display="block" $marginBottom="XS">
                  Dane firmy
                </Label>
                <InfoContent>
                  <P label="Nazwa firmy" $marginBottom="0">
                    {data.company.name}
                  </P>
                  <P label="NIP" $marginBottom="0">
                    {data.company.number}
                  </P>
                  <P label="Adres" $marginBottom="0">
                    {data.company.address}
                  </P>
                  <P label="Miasto" $marginBottom="0">
                    {data.company.city}
                  </P>
                  <P label="Kod pocztowy" $marginBottom="0">
                    {data.company.postCode}
                  </P>
                  <P label="Kraj" $marginBottom="0">
                    {translate(data.company.countryCode.toUpperCase())}
                  </P>
                </InfoContent>
              </InfoWrapper>
            )}
            <InfoWrapper>
              <Label $display="block" $marginBottom="XS">
                Funkcje premium
              </Label>
              {PROFILE_FEATURE_FLAGS_ARRAY.map(([title, flag, text]) => {
                const active = (data.featureFlags ?? []).includes(flag)
                return (
                  <Box key={flag} $marginBottom="XS">
                    <SwitchRow
                      name={flag}
                      title={title}
                      text={text}
                      value={active}
                      disabled={pendingFlag !== null && pendingFlag !== flag}
                      onChange={handleToggleFlag}
                    />
                  </Box>
                )
              })}
            </InfoWrapper>
            <Link icon="plus" $type="secondary" to={`/add-offer/${data.id}`}>
              Dodaj ogłoszenie
            </Link>
            <Button
              icon="lock"
              $type="secondary"
              loading={generating}
              onClick={handleGenerateToken}
              $marginTop="S"
              $width="100%"
            >
              Zaloguj się na konto
            </Button>
            {/*<Button icon="info-circle" type="secondary" marginTop="S" width="100%" onClick={handleChangeToAccount}>*/}
            {/*  Zamień na konto pracownicze*/}
            {/*</Button>*/}
            <Button
              icon="trash"
              $marginTop="S"
              $width="100%"
              $background="red"
              $color="white"
              $hoverBackground="red"
              $hoverColor="white"
              onClick={handleDelete}
            >
              Usuń profil
            </Button>
          </Wrapper>
        )}
      </>
    </Modal>
  )
}

export default AdminProfileModal
